import cx from 'classnames'
import { isNil, map, omitBy } from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import {
  postToPostContextSchema,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import { PostLocation, PostType } from 'lib/_types'
import { toggleBlockUserConfirmBox } from 'store/actions/confirmBox'
import { useGetGroupQuery } from 'store/api/yay/group'
import {
  useCreateChatRoomReportMutation,
  useCreateGroupReportMutation,
  useCreatePostReportMutation,
  useCreateUserReportMutation,
} from 'store/api/yay/report'
import { useCurrentUser, useGetUserQuery } from 'store/api/yay/user'
import { useModal } from 'store/slices/modals'
import { useUploadImage, useGroupIcon } from 'hooks'

import Avatar from 'components/Avatar'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import Nickname from 'components/Nickname'

const MAX_PROBLEM_LENGTH = 200

type ReportModalData = {
  chatRoomId?: string
  groupId?: string
  userId?: string
  postId?: string
  postType?: PostType
  postLocation?: string
  postIndex?: number
}

export const useReportModal = () => useModal<ReportModalData>('ReportModal')

const ReportModalForm = ({
  modalData,
  onClose,
  __formErrorSet,
  __confirmBoxSet,
}: {
  modalData: ReportModalData
  onClose: Function
  __formErrorSet: Function
  __confirmBoxSet: Function
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { upload } = useUploadImage()
  const { data: currentUser } = useCurrentUser()
  const { getGroupIcon } = useGroupIcon()

  const {
    chatRoomId,
    groupId,
    userId,
    postId,
    postType,
    postLocation,
    postIndex,
  } = modalData

  const userRes = useGetUserQuery({ userId }, { skip: isNil(userId) })
  const groupRes = useGetGroupQuery({ groupId }, { skip: isNil(groupId) })

  const [createUserReport] = useCreateUserReportMutation()
  const [createChatRoomReport] = useCreateChatRoomReportMutation()
  const [createPostReport] = useCreatePostReportMutation()
  const [createGroupReport] = useCreateGroupReportMutation()

  const [__isLoading, __isLoadingSet] = useState(false)
  const [__reason, __reasonSet] = useState('')
  const [__screenshots, __screenshotsSet] = useState({})

  return (
    <form
      className="Modal__wrapper"
      onSubmit={async (e: React.SyntheticEvent) => {
        e.preventDefault()

        const $els = e.target as typeof e.target & {
          category_id: { value: string }
        }

        let postMethod = createUserReport

        if (chatRoomId) {
          postMethod = createChatRoomReport
        } else if (postId) {
          postMethod = createPostReport
        } else if (groupId) {
          postMethod = createGroupReport
        }

        if ($els.category_id.value === 'not-selected') {
          __formErrorSet({
            isActive: true,
            title: t('report_modal.error.no_report_topic_selected'),
          })

          return
        }

        __isLoadingSet(true)

        try {
          const screenshots = map(__screenshots, (x) => x)

          let reportData = {
            ...omitBy(
              {
                postId,
                chatRoomId,
                groupId,
                userId: !chatRoomId && !postId && !groupId ? userId : null,
                opponent_id: chatRoomId || postId || groupId ? userId : null,
                category_id: $els.category_id.value,
                email: currentUser?.email,
              },
              (x) => !x
            ),
            reason: __reason,
          }

          if (screenshots?.length > 0) {
            const resUpload = await upload(screenshots, 'report')

            resUpload
              ?.filter?.((x) => !x?.isThumb)
              .forEach?.((x, i) => {
                reportData = {
                  ...reportData,
                  [i == 0
                    ? 'screenshot_filename'
                    : `screenshot_${i + 1}_filename`]: x?.fileName,
                }
              })
          }

          const res = await postMethod(reportData)

          if (res?.['data']?.result == 'success') {
            if (postMethod === createPostReport) {
              snowplowEvent.report_post({
                user: userToUserContextSchemaData({ user: userRes?.data }),
                post: postToPostContextSchema({
                  post: {
                    id: +postId,
                    post_type: (postType as PostType) ?? 'text',
                    user: {
                      id: +userId,
                    },
                  },
                  location: (postLocation as PostLocation) ?? 'timeline',
                  position: +postIndex > -1 ? +postIndex + 1 : undefined,
                }),
              })
            } else if (postMethod === createUserReport) {
              snowplowEvent.report_user({
                user: userToUserContextSchemaData({ user: userRes?.data }),
              })
            }

            if (!chatRoomId && !postId && !groupId) {
              dispatch(
                toggleBlockUserConfirmBox({
                  user: userRes?.data,
                  onClose,
                })
              )
            } else {
              __confirmBoxSet('reportSent')
            }
          }
        } finally {
          __isLoadingSet(false)
        }
      }}
    >
      <div className="Modal__header">
        <a
          className={cx('Modal__header__close', {
            'Modal__header__close--back': chatRoomId,
          })}
          onClick={() => onClose()}
        />
        <h2 className="Modal__header__h2">{t('common.report')}</h2>
      </div>

      <div className="Modal__body">
        <div className="ReportChatroom">
          <div className="ReportChatroom__header">
            {(() => {
              const groupIcon = getGroupIcon({
                group: groupRes?.data,
              })

              return (
                <Avatar
                  user={
                    groupId
                      ? {
                          profile_icon_thumbnail:
                            groupIcon?.icon ?? groupIcon?.placeholder,
                        }
                      : userRes?.data
                  }
                />
              )
            })()}

            <div>
              <h3 className="ReportChatroom__header__h3">
                {groupId ? (
                  groupRes?.data?.topic
                ) : (
                  <Nickname user={userRes?.data} />
                )}
              </h3>

              <p className="ReportChatroom__header__p">
                {groupId
                  ? groupRes?.data?.description
                  : userRes?.data?.biography?.trim?.()}
              </p>
            </div>
          </div>

          <div className="ReportChatroom__body">
            <label className="ReportChatroom__body__label">
              {t('report_modal.screenshots.label')}
            </label>

            <div className="ReportChatroom__body__photos">
              {useMemo(
                () =>
                  Array.from(Array(4)).map((_, i) => (
                    <label
                      key={i}
                      className={cx('ReportChatroom__body__photos__label', {
                        'ReportChatroom__body__photos__label--placeholder':
                          isNil(__screenshots?.[i]),
                        'ReportChatroom__body__photos__label--x-disabled':
                          i > Object.keys(__screenshots)?.length,
                      })}
                      style={{
                        backgroundImage: __screenshots?.[i]
                          ? `url(${URL.createObjectURL(__screenshots?.[i])})`
                          : 'none',
                      }}
                    >
                      <svg viewBox="0 0 1 1" />

                      <input
                        type="file"
                        accept=".jpg, .jpeg, .png"
                        name="screenshot"
                        onChange={(e) => {
                          __screenshotsSet((prevState) => {
                            return {
                              ...prevState,
                              [i]: e.target.files[0],
                            }
                          })
                        }}
                      />
                    </label>
                  )),
                [__screenshots]
              )}
            </div>

            <label className="ReportChatroom__body__label">
              {t('report_modal.report_topic.label')}
            </label>

            <select className="Select" name="category_id">
              <option value="not-selected">
                {t('report_modal.report_topic.hint_text')}
              </option>

              {[
                t('report_modal.report_topic.rude'),
                t('report_modal.report_topic.spam'),
                t('report_modal.report_topic.convince'),
                t('report_modal.report_topic.seller'),
                t('report_modal.report_topic.purpose'),
                t('report_modal.report_topic.fake'),
                t('report_modal.report_topic.danger'),
              ].map((x, i) => (
                <option key={i} value={i}>
                  {x}
                </option>
              ))}
            </select>

            <label className="ReportChatroom__body__label">
              {t('report_modal.report_reason.label')}
            </label>

            <textarea
              className="Input"
              rows={5}
              value={__reason}
              onChange={(e) => {
                __reasonSet(e.target.value.slice(0, MAX_PROBLEM_LENGTH))
              }}
              placeholder={t('report_modal.report_reason.placeholder')}
            />

            <p className={cx('ReportChatroom__body__hint')}>
              {__reason?.length}/{MAX_PROBLEM_LENGTH}
            </p>
          </div>
        </div>
      </div>

      <div className="Modal__footer">
        <button
          className={cx('Button', {
            'Button--green': true,
            'Button--icon-chat-send': true,
            'Button--disabled': __isLoading,
          })}
        >
          {t('common.send')}
        </button>
      </div>
    </form>
  )
}

const ReportModal = () => {
  const { t } = useTranslation()
  const modal = useReportModal()

  const { onClose } = modal

  const [__isLoading, __isLoadingSet] = useState(false)
  const [__reason, __reasonSet] = useState('')
  const [__screenshots, __screenshotsSet] = useState({})

  const [__formError, __formErrorSet] = useState({
    isActive: false,
    title: null,
  })

  const [__confirmBox, __confirmBoxSet] = useState(null)

  return (
    <div
      className={cx('Modal ReportModal', {
        'Modal--active': modal.isActive,
      })}
    >
      <a className="Modal__overlay" onClick={() => onClose()} />

      {modal.isActive && (
        <ReportModalForm
          modalData={modal.data}
          onClose={onClose}
          __formErrorSet={__formErrorSet}
          __confirmBoxSet={__confirmBoxSet}
        />
      )}

      {(() => {
        const _onClose = () => {
          __formErrorSet((prevState) => {
            return {
              ...prevState,
              isActive: false,
            }
          })
        }

        return (
          <ConfirmBoxBasic
            isActive={__formError?.isActive}
            title={__formError?.title}
            onOk={_onClose}
            onClose={_onClose}
          />
        )
      })()}

      {(() => {
        const _onClose = () => {
          __confirmBoxSet(null)
          onClose()
        }

        return (
          <ConfirmBoxBasic
            isActive={__confirmBox == 'reportSent'}
            onClose={_onClose}
            onOk={_onClose}
            title={t('common.reported')}
            content={t('report_modal.report_sent_confirm_box.body')}
          />
        )
      })()}
    </div>
  )
}

export default ReportModal
